// Attaching files (protocol 1.5, feature `attachments`), on the phone page and in
// `mountVoiceUI({attachments})`: pick (or drop, or paste) a screenshot, a photo or any other file
// (a document, a spreadsheet, an archive, a program: every type is taken, only the size is
// limited); it is prepared in the page (pictures scaled down to IMAGE_MAX_SIDE and re-compressed,
// a thumbnail made; other files go as they are), uploaded to the kit right away in acknowledged
// chunks, and staged for the next turn. It goes with the next typed message (named in
// `user.text`), or, if the user speaks first, with the next spoken turn. A chip shows each file
// until then.
//
// `AttachTray` holds that state with no DOM, so it can be tested; phone/main.ts and app.ts draw it.
import { ClientError, type Upload, type VoiceKitClient } from "./client";

/** Files waiting at once (the kit's limit too). */
export const MAX_FILES = 10;
/** The kit's per-file limit. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** Pictures are scaled down so the longest side is at most this. */
export const IMAGE_MAX_SIDE = 2000;
export const IMAGE_QUALITY = 0.85;
/** Pictures at most this big (and within IMAGE_MAX_SIDE) are sent as they are. */
export const IMAGE_KEEP_BYTES = 1.5 * 1024 * 1024;
export const THUMB_SIDE = 160;
/**
 * What the picker offers: every file (an empty `accept`, so no filter). Phones still offer the
 * photo library and the camera next to their files.
 */
export const ACCEPT = "";
/** The type a file goes as when neither the browser nor its name says what it is. */
export const UNKNOWN_MIME = "application/octet-stream";

/** Types for names the browser gives no type (phones often don't). Anything else is UNKNOWN_MIME. */
const BY_EXT: Record<string, string> = {
  pdf: "application/pdf",
  txt: "text/plain",
  log: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  csv: "text/csv",
  json: "application/json",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
};

/** A media type the kit takes (`type/subtype`, as its `clean_mime` checks), lower case, no parameters. */
function mediaType(type: string): string | null {
  const t = type.split(";")[0].trim().toLowerCase();
  return t.length <= 100 && /^[a-z0-9+.-]+\/[a-z0-9+.-]+$/.test(t) ? t : null;
}

/**
 * The media type to send a file as. Every file is taken: the browser's type when it gives a
 * usable one, else one from the name (`.md`, `.heic`, ...), else `application/octet-stream`.
 */
export function acceptedMime(name: string, type: string): string {
  const t = mediaType(type);
  if (t) return t;
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase();
  return (ext && BY_EXT[ext]) || UNKNOWN_MIME;
}

/** Why a file can't be attached (only its size, any type is taken), or `null`. */
export function rejectReason(name: string, type: string, size: number): string | null {
  const mime = acceptedMime(name, type);
  // Pictures are made smaller before sending; other files go as they are.
  const limit = mime.startsWith("image/") ? 4 * MAX_FILE_BYTES : MAX_FILE_BYTES;
  if (size > limit) return `${name || "This file"} is too big (${mb(size)} MB; the limit is ${mb(limit)} MB).`;
  return null;
}

/** `w`x`h` scaled down (never up) so the longest side is at most `max`. */
export function fitSize(w: number, h: number, max: number): { width: number; height: number } {
  const k = Math.min(1, max / Math.max(w, h, 1));
  return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)) };
}

/** A picture that can go as it is: a web format, small enough in pixels and bytes. */
export function keepImageAsIs(mime: string, w: number, h: number, bytes: number): boolean {
  const web = ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(mime);
  return web && Math.max(w, h) <= IMAGE_MAX_SIDE && bytes <= IMAGE_KEEP_BYTES;
}

function mb(bytes: number): string {
  const v = bytes / (1024 * 1024);
  return v >= 10 ? v.toFixed(0) : v.toFixed(1);
}

/** A file ready to upload: its bytes (pictures already made smaller) and a thumbnail. */
export type Prepared = Omit<Upload, "id">;

/** Turns a picked file into what is uploaded. The browser version is `prepareFile`. */
export type Preparer = (file: File) => Promise<Prepared>;

export type ChipState = "preparing" | "uploading" | "ready" | "failed";

export interface Chip {
  id: string;
  name: string;
  mime: string;
  state: ChipState;
  /** Upload progress, 0..1. */
  progress: number;
  thumb?: string;
  error?: string;
}

type Kit = Pick<VoiceKitClient, "upload" | "stageAttachments">;

let seq = 0;
function newId(): string {
  return `f${Date.now().toString(36)}${(++seq).toString(36)}`;
}

/** The files waiting to be sent, and their uploads. */
export class AttachTray {
  chips: Chip[] = [];
  /** Called on every change (draw the chips). */
  onChange: () => void = () => {};
  /** A message for the user (a file refused, an upload that failed). */
  onError: (message: string) => void = () => {};

  private readonly files = new Map<string, Prepared>();
  private queue: string[] = [];
  private uploading: string | null = null;
  /** Uploads finished on the current connection (a new leg starts empty). */
  private uploaded = new Set<string>();
  private generation = 0;

  /**
   * `outdated`: what to say when the kit doesn't take attachments (the phone page: update the
   * app on the PC; elsewhere: the kit). Default: the generic {@link OUTDATED_KIT}.
   */
  constructor(
    private kit: () => Kit | null,
    private readonly prepare: Preparer,
    private readonly outdated: string = OUTDATED_KIT,
  ) {}

  /** Ids of the files that are uploaded and will go with the next turn. */
  readyIds(): string[] {
    return this.chips.filter((c) => c.state === "ready").map((c) => c.id);
  }

  /** A file is still being prepared or uploaded. */
  get busy(): boolean {
    return this.chips.some((c) => c.state === "preparing" || c.state === "uploading");
  }

  /** Adds picked files: each is checked, prepared, then uploaded. */
  add(files: File[]): void {
    for (const file of files) {
      if (this.chips.length >= MAX_FILES) {
        this.onError(`You can attach up to ${MAX_FILES} files at a time.`);
        break;
      }
      const why = rejectReason(file.name, file.type, file.size);
      if (why) {
        this.onError(why);
        continue;
      }
      const chip: Chip = { id: newId(), name: file.name || "file", mime: acceptedMime(file.name, file.type), state: "preparing", progress: 0 };
      this.chips.push(chip);
      this.prepare(file).then(
        (p) => {
          if (!this.chips.includes(chip)) return; // removed meanwhile
          if (p.bytes.length > MAX_FILE_BYTES) return this.fail(chip, `${chip.name} is too big even after shrinking it.`);
          // Always a type the kit takes, whatever the preparer said (none for an unknown file).
          const name = p.name || chip.name;
          const mime = acceptedMime(name, p.mime);
          this.files.set(chip.id, { ...p, name, mime });
          Object.assign(chip, { name, mime, thumb: p.thumb, state: "uploading" as const });
          this.enqueue(chip.id);
        },
        (e: Error) => this.fail(chip, `Couldn't read ${chip.name}: ${e.message}`),
      );
    }
    this.onChange();
  }

  /** The user removed a file. */
  remove(id: string): void {
    const had = this.chips.find((c) => c.id === id);
    if (!had) return;
    this.chips = this.chips.filter((c) => c.id !== id);
    this.files.delete(id);
    this.queue = this.queue.filter((q) => q !== id);
    if (had.state === "ready") this.stage();
    this.onChange();
  }

  /** These files went with a turn (`stt.final.attachments`, or a typed message that was sent). */
  sent(ids: string[]): void {
    const gone = new Set(ids);
    if (!this.chips.some((c) => gone.has(c.id))) return;
    this.chips = this.chips.filter((c) => !gone.has(c.id));
    for (const id of ids) {
      this.files.delete(id);
      this.uploaded.delete(id);
    }
    this.onChange();
  }

  /** A new connection to the PC (`welcome`): what was uploaded on the old one is uploaded again. */
  reconnected(): void {
    this.generation++;
    this.uploaded.clear();
    this.uploading = null;
    this.queue = [];
    for (const c of this.chips) {
      if (c.state === "ready" || c.state === "uploading") {
        c.state = "uploading";
        c.progress = 0;
        this.queue.push(c.id);
      }
    }
    this.onChange();
    void this.pump();
  }

  private fail(chip: Chip, message: string): void {
    chip.state = "failed";
    chip.error = message;
    this.files.delete(chip.id);
    this.onError(message);
    this.onChange();
  }

  private enqueue(id: string): void {
    this.queue.push(id);
    this.onChange();
    void this.pump();
  }

  private async pump(): Promise<void> {
    if (this.uploading) return;
    const gen = this.generation;
    while (this.queue.length && gen === this.generation) {
      const id = this.queue.shift()!;
      const file = this.files.get(id);
      const chip = this.chips.find((c) => c.id === id);
      const kit = this.kit();
      if (!file || !chip) continue;
      if (!kit) {
        this.queue.unshift(id); // not connected: reconnected() starts over
        return;
      }
      this.uploading = id;
      const stale = () => gen !== this.generation || !this.chips.includes(chip);
      try {
        await kit.upload({ id, ...file }, (f) => {
          chip.progress = f;
          this.onChange();
        }, stale);
        if (stale()) continue;
        chip.state = "ready";
        this.uploaded.add(id);
        this.stage();
      } catch (e) {
        if (stale()) continue;
        const err = e as ClientError;
        if (err.code === "disconnected") {
          chip.state = "uploading"; // goes again when the connection is back (reconnected())
          chip.progress = 0;
        } else this.fail(chip, uploadError(chip.name, err, this.outdated));
      } finally {
        if (this.uploading === id) this.uploading = null;
        this.onChange();
      }
    }
  }

  /** Tell the kit which files go with the next turn (also a spoken one). */
  private stage(): void {
    const kit = this.kit();
    const ids = this.readyIds().filter((id) => this.uploaded.has(id));
    void kit?.stageAttachments(ids).catch(() => {});
  }
}

/** What to say when the kit doesn't take attachments, outside the phone page. */
export const OUTDATED_KIT = "This voice kit doesn't take attachments yet. Update it to attach files.";
/** The phone page's: the kit is the PC's. */
export const OUTDATED_PC = "This PC's voice kit doesn't take attachments yet. Update the app on your PC.";

/** What to show when an upload fails. `outdated`: what to say when the kit can't take files. */
export function uploadError(name: string, e: { code?: string; message: string }, outdated: string = OUTDATED_KIT): string {
  switch (e.code) {
    case "attachment_too_large":
    case "too_many_attachments":
      return e.message.charAt(0).toUpperCase() + e.message.slice(1) + ".";
    case "forbidden":
    case "unknown_type":
    case "feature_not_declared":
      return outdated;
    default:
      return `Couldn't send ${name}: ${e.message}`;
  }
}

// -- drops and pastes --------------------------------------------------------------------------

/** A drag that carries files (from Explorer, Finder, another app). */
export function hasFiles(dt: DataTransfer | null | undefined): boolean {
  return !!dt && Array.from(dt.types ?? []).includes("Files");
}

/** Names a clipboard picture gets from the browser ("image.png"), or none. */
const GENERIC_PASTE = /^(image|blob)?\.?(png|jpe?g|gif|webp|bmp)?$/i;

/** Screenshot names given out so far (two in the same second get "(2)", "(3)"...). */
const named = new Map<string, number>();

/**
 * The files a drop or a paste carries, as they are. With `paste`, a clipboard picture (which the
 * browser calls "image.png") is named for when it was pasted ("Screenshot 2026-10-13
 * 15.04.05.png", then "... (2).png" within the same second), so several in one conversation stay
 * apart. A dropped file keeps its own name, even "image.png".
 */
export function transferFiles(
  dt: DataTransfer | null | undefined,
  opts: { paste?: boolean; now?: Date } = {},
): File[] {
  if (!dt) return [];
  let files = Array.from(dt.files ?? []);
  if (!files.length && dt.items) {
    files = Array.from(dt.items)
      .filter((i) => i.kind === "file")
      .map((i) => i.getAsFile())
      .filter((f): f is File => !!f);
  }
  if (!opts.paste) return files;
  const base = `Screenshot ${stamp(opts.now ?? new Date())}`;
  return files.map((f) => {
    if (!f.type.startsWith("image/") || !GENERIC_PASTE.test(f.name)) return f;
    const ext = f.type === "image/jpeg" ? "jpg" : f.type.slice("image/".length).replace(/[^a-z0-9]/gi, "") || "png";
    const n = (named.get(base) ?? 0) + 1;
    named.set(base, n);
    if (named.size > 50) named.delete(named.keys().next().value!);
    const name = n === 1 ? `${base}.${ext}` : `${base} (${n}).${ext}`;
    return new File([f], name, { type: f.type, lastModified: f.lastModified });
  });
}

/** Text the clipboard holds besides any files (copying cells or a web page puts a picture there too). */
export function pastedText(dt: DataTransfer | null | undefined): string {
  try {
    return dt?.getData("text/plain") ?? "";
  } catch {
    return "";
  }
}

function stamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}.${p(d.getMinutes())}.${p(d.getSeconds())}`;
}

// -- the browser's part: decoding, scaling and compressing pictures ----------------------------

/**
 * Prepares a picked file in the browser: pictures made smaller (and HEIC made JPEG); any other
 * file, and a picture this browser can't open (TIFF, SVG, ...), as it is. Nothing is opened or
 * run: a file that isn't a picture is only read as bytes.
 */
export async function prepareFile(file: File): Promise<Prepared> {
  const name = file.name || "file";
  const mime = acceptedMime(name, file.type);
  const asIs = async (): Promise<Prepared> => {
    if (file.size > MAX_FILE_BYTES) throw new Error(`it is over ${mb(MAX_FILE_BYTES)} MB`);
    return { name, mime, bytes: new Uint8Array(await file.arrayBuffer()) };
  };
  if (!mime.startsWith("image/")) return asIs();
  let img: Decoded;
  try {
    img = await decodeImage(file);
  } catch (e) {
    if (file.size <= MAX_FILE_BYTES) return asIs(); // sent as a file, with no thumbnail
    throw e;
  }
  try {
    const thumb = drawTo(img, THUMB_SIDE).toDataURL("image/jpeg", 0.7);
    if (keepImageAsIs(mime, img.width, img.height, file.size)) {
      return { name, mime, bytes: new Uint8Array(await file.arrayBuffer()), thumb };
    }
    const blob = await toBlob(drawTo(img, IMAGE_MAX_SIDE), "image/jpeg", IMAGE_QUALITY);
    const jpg = name.replace(/\.[a-z0-9]+$/i, "") + ".jpg";
    return { name: jpg, mime: "image/jpeg", bytes: new Uint8Array(await blob.arrayBuffer()), thumb };
  } finally {
    if ("close" in img) img.close();
  }
}

type Decoded = ImageBitmap | HTMLImageElement;

async function decodeImage(file: File): Promise<Decoded> {
  // Turned the right way up (EXIF orientation), in every browser this page supports.
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Older Safari: through an <img>. The relay's CSP allows data: images, not blob: ones.
    const url = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(new Error("unreadable"));
      r.readAsDataURL(file);
    });
    const img = new Image();
    img.src = url;
    await img.decode().catch(() => {
      throw new Error("not a picture this phone can open");
    });
    return img;
  }
}

function drawTo(img: Decoded, max: number): HTMLCanvasElement {
  const w = "naturalWidth" in img ? img.naturalWidth : img.width;
  const h = "naturalHeight" in img ? img.naturalHeight : img.height;
  const size = fitSize(w, h, max);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff"; // transparent pixels: white, not black, in a JPEG
  ctx.fillRect(0, 0, size.width, size.height);
  ctx.drawImage(img, 0, 0, size.width, size.height);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("couldn't compress the picture"))), type, quality),
  );
}
